import React from 'react';
import { 
    Book, 
    User, 
    ShieldCheck, 
    Package, 
    CreditCard,
    Phone,
    FileText,
    RefreshCw,
    Lock,
    Info,
    Eye,
    AlertTriangle,
    Scale,
    MessageSquare,
    Store,
    Gavel,
    Copyright,
    Shield,
    Star,
    DollarSign,
    MailOpen,
    Image,
    Edit,
    FileEdit,
    X,
    MapPin
} from 'lucide-react';
import { TermsMetaData } from '../layouts/MetaData/TermsMetaData';

const TermsSection = ({ title, children, icon: Icon }) => (
    <section className="bg-white rounded-lg p-6 shadow-md mb-6">
        <div className="flex items-center mb-3">
            <Icon className="w-6 h-6 text-red-600 mr-3" />
            <h2 className="text-lg font-semibold text-red-600">{title}</h2>
        </div>
        {children}
    </section>
);

const Terms = () => {
    return (
        <>
            <TermsMetaData/>
            <div 
                className="pt-0"
                style={{
                   backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">
                            Terms and Conditions
                        </h1>
                        <div className="text-base text-gray-600 max-w-3xl mx-auto">
                            <p>
                                These Terms and Conditions ("Terms") govern your use of the Meatbuddy platform, operated by Vistara Farms Private Limited. By accessing or using the Platform, you agree to abide by these Terms. If you do not agree, please refrain from using our services.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6 max-w-4xl mx-auto">
                        <TermsSection title="1. Introduction" icon={Info}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                These Terms and Conditions ("Terms") govern your use of the Meatbuddy platform, operated by Vistara Farms Private Limited. The platform includes the website (www.meatbuddy.in) and mobile applications on iOS and Android (collectively, the "Platform"). By accessing or using the Platform, you agree to abide by these Terms. If you do not agree, please refrain from using our services.
                            </p>
                        </TermsSection>

                        <TermsSection title="2. Customer Registration and Privacy" icon={User}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">To access certain features and place orders, you must provide accurate and complete personal information, including:</p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Name</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Email address</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Mobile phone number</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Postal pin code</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Personal demographic details (age, gender, location, etc.)</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs leading-relaxed mt-3">
                                By registering or placing an order, you consent to receiving promotional and transactional communications from Meatbuddy. You may opt out by contacting customersupport@meatbuddy.in
                            </p>
                            <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                                <p className="text-gray-600 text-xs font-semibold">Privacy Commitment:</p>
                                <ul className="list-none space-y-2 mt-2">
                                    <li className="flex items-start gap-2">
                                        <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                        <span className="text-gray-600 text-xs">Your data is never sold to third parties</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                        <span className="text-gray-600 text-xs">However, our platform may contain links to third-party services (e.g., Google Analytics), for which we are not responsible regarding privacy policies</span>
                                    </li>
                                </ul>
                            </div>
                        </TermsSection>

                        <TermsSection title="3. Eligibility" icon={ShieldCheck}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                To use the Platform, you must:
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Be legally capable of entering into a contract under Section 11 of the Indian Contract Act, 1872</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Be at least 18 years old</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Not be disqualified by law from entering into contracts</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs leading-relaxed mt-3">
                                If you are under 18 years old, you may use the Platform only under the supervision of a parent or legal guardian.
                            </p>
                        </TermsSection>

                        <TermsSection title="4. Platform Access & Usage" icon={Eye}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                <strong>Authorized Use</strong>: Meatbuddy grants a limited, non-transferable, revocable right to access and use the Platform for personal, non-commercial purposes.
                            </p>
                        </TermsSection>

                        <TermsSection title="5. Pricing & Payments" icon={CreditCard}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Prices on www.meatbuddy.in are exclusive of GST, unless otherwise stated.
                            </p>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Fresh food pricing may vary based on the delivery date; additional charges or refunds may apply.
                            </p>
                            <p className="text-gray-600 text-xs font-semibold mb-2">Payment Methods:</p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Credit/Debit Cards</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">UPI & Net Banking</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Mobile Wallets</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Cash on Delivery (COD) (where applicable)</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs leading-relaxed mt-3">
                                Meatbuddy reserves the right to change prices, offers, and discounts without prior notice.
                            </p>
                        </TermsSection>

                        <TermsSection title="6. Delivery & Handling Charges" icon={Package}>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Delivery charges may vary based on location and order value</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Estimated delivery times are subject to change due to unforeseen circumstances. Customers will be notified of any significant delays</span>
                                </li>
                            </ul>
                        </TermsSection>

                        <TermsSection title="7. Order Cancellation" icon={RefreshCw}>
                            <p className="text-gray-600 text-xs font-semibold mb-2">Cancellation by Customer:</p>
                            <ul className="list-none space-y-2 mb-3">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Orders can be cancelled within 1 hour of placement, except for Express Delivery orders</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Once an order is processed or dispatched, cancellation is not possible</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs font-semibold mb-2">Cancellation by Meatbuddy:</p>
                            <p className="text-gray-600 text-xs mb-2">Meatbuddy may cancel orders due to:</p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Product unavailability</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Payment issues</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Suspected fraudulent activity</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs mt-3">
                                Customers will be duly notified in such cases, and refunds will be processed accordingly.
                            </p>
                        </TermsSection>

                        <TermsSection title="8. Returns & Refunds" icon={Store}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                <strong>Hassle-Free Returns</strong>: We offer a "No Questions Asked" return and refund policy for quality-related concerns.
                            </p>
                            <p className="text-gray-600 text-xs font-semibold mb-2">Refund Eligibility:</p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Requests must be made within 24 hours of delivery</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Refunds may be issued as store credit or reversed to the original payment method, as applicable</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs mt-3">
                                For more details, please refer to our Refund Policy.
                            </p>
                        </TermsSection>

                        <TermsSection title="9. Store Credits" icon={DollarSign}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Customers may receive Meatbuddy Cash (Store Credit), subject to regional policies.
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Store credit is non-transferable and cannot be exchanged for cash</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Unused store credits may expire as per policy terms</span>
                                </li>
                            </ul>
                        </TermsSection>

                        <TermsSection title="10. Customer Obligations" icon={Scale}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                By using the Platform, customers agree to:
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Provide accurate and updated personal information</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Bear additional costs incurred due to incorrect addresses or re-delivery</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Accept potential weight variances in fresh meat products</span>
                                </li>
                            </ul>
                        </TermsSection>

                        <TermsSection title="11. Prohibited Activities" icon={X}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Customers must NOT:
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Share, transmit, or upload objectionable or unlawful content</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Infringe upon any intellectual property rights</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Engage in fraudulent, deceptive, or illegal activities</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Disrupt or interfere with the functionality of the Platform</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs mt-3">
                                Violations may result in legal action and permanent account suspension.
                            </p>
                        </TermsSection>

                        <TermsSection title="12. Customer Reviews & Feedback" icon={Star}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Customers may submit honest reviews, but they must NOT:
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Post false, misleading, abusive, defamatory, or offensive content</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Violate privacy, copyright, or legal standards</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs mt-3">
                                Meatbuddy reserves the right to moderate, edit, or remove inappropriate reviews. In cases where a review leads to irreparable damage to Meatbuddy's reputation, business operations, or customer trust, Meatbuddy reserves the right to pursue legal action against the responsible party.
                            </p>
                        </TermsSection>

                        <TermsSection title="13. Opting Out of Marketing Communications" icon={MailOpen}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Customers can unsubscribe by:
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
                                    <span className="text-gray-600 text-xs">Emailing - customersupport@meatbuddy.in</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <Phone className="w-4 h-4 text-red-600 flex-shrink-0" />
                                    <span className="text-gray-600 text-xs">Calling - +91- 6364749404</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-red-600 flex-shrink-0" />
                                    <div className="text-gray-600 text-xs">
                                        <p>Visiting - Vistara Farms Private Limited, No-158, Padma Upadhyaya Layout, Nagadevanahalli, Kengeri, Bangalore — 560056</p>
                                        <p className="italic">(Monday to Friday 9:00 AM to 6:00 PM)</p>
                                    </div>
                                </li>
                            </ul>
                        </TermsSection>

                        <TermsSection title="14. Product Images & Colours" icon={Image}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                Product images on the Platform are representative. Actual colours, packaging, and appearance may vary due to lighting, device screens, or supplier changes. Meatbuddy is not liable for such discrepancies.
                            </p>
                        </TermsSection>

                        <TermsSection title="15. Modification of Terms & Conditions" icon={FileEdit}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                Meatbuddy reserves the right to modify, update, or discontinue these Terms at any time. Continued use of the Platform following updates indicates acceptance of the revised Terms.
                            </p>
                        </TermsSection>

                        <TermsSection title="16. Governing Law & Dispute Resolution" icon={Gavel}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                These Terms shall be governed by Indian laws. Disputes shall first be resolved through mutual discussions. If unresolved, disputes will be settled via arbitration under the Arbitration and Conciliation Act, 1996. The arbitration will be conducted in English, in Bangalore.
                            </p>
                            <p className="text-gray-600 text-xs leading-relaxed mt-3">
                                The courts of Bangalore shall have exclusive jurisdiction over legal matters.
                            </p>
                        </TermsSection>

                        <TermsSection title="17. Intellectual Property Rights" icon={Copyright}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                All content, text, technology, and trademarks on the Platform are the intellectual property of Meatbuddy. No rights to use, modify, or distribute such content are granted without explicit permission.
                            </p>
                        </TermsSection>

                        <TermsSection title="18. Content Disclaimer" icon={AlertTriangle}>
                            <p className="text-gray-600 text-xs leading-relaxed">
                                Meatbuddy takes reasonable measures to ensure appropriate content on the Platform. However, we are not responsible for content that some users may find offensive.
                            </p>
                        </TermsSection>

                        <TermsSection title="19. Indemnification" icon={Shield}>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Customers agree to indemnify Meatbuddy, its employees, and affiliates against any claims, liabilities, or damages arising from:
                            </p>
                            <ul className="list-none space-y-2">
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Breach of these Terms</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Violation of applicable laws</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="mt-1 min-w-[8px] h-2 w-2 rounded-full bg-red-600" />
                                    <span className="text-gray-600 text-xs">Unauthorized use of the Platform</span>
                                </li>
                            </ul>
                            <p className="text-gray-600 text-xs mt-3">
                                This indemnity clause survives account termination.
                            </p>
                        </TermsSection>

                        <TermsSection title="Contact Information" icon={Phone}>
                            <div className="space-y-3">
                                <p className="text-gray-600 text-xs leading-relaxed">
                                    For any further queries, please contact our support team at:
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-red-600" />
                                        <p className="text-red-600 font-medium text-sm">customersupport@meatbuddy.in</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-5 h-5 text-red-600" />
                                        <p className="text-gray-600 text-xs">+91- 6364749404</p>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <MapPin className="w-5 h-5 text-red-600 mt-0.5" />
                                        <p className="text-gray-600 text-xs">
                                            Vistara Farms Private Limited, No-158, Padma Upadhyaya Layout, Nagadevanahalli, Kengeri, Bangalore — 560056
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </TermsSection>

                        {/* Call to Action */}
                        <div className="text-center">
                            <p className="text-base text-gray-600 mb-5">
                                By using our website and services, you agree to these Terms and Conditions. If you have any questions, please contact our customer support team.
                            </p>
                            <a 
                                href="/contact" 
                                className="inline-block bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                            >
                                Contact Us
                            </a>
                        </div>

                        <div className="text-center text-xs text-gray-500 pt-6">
                            Last updated: March 03, 2025
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Terms;